import { useMemo } from 'react';
import { useAuthToken } from './useAuthToken';

interface UserPayload {
  sub: string;
  email: string;
  organization_id: string | null;
  organization_slug?: string | null;
  user_uid: string;
  role: 'admin' | 'user' | null;
  is_new_user?: boolean;
  is_platform?: boolean;
  is_partner_admin?: boolean;
  has_impersonation_grants?: boolean;
  has_platform_admin_membership?: boolean;
  iat: number;
  exp: number;
}

/**
 * JWTトークンからユーザー情報をデコードして返すフック
 */
export function useUser() {
  const { token } = useAuthToken();

  const user = useMemo<UserPayload | null>(() => {
    if (!token) {
      return null;
    }

    try {
      // JWTは header.payload.signature の形式
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('[useUser] Invalid JWT format: expected 3 parts, got', parts.length);
        return null;
      }

      // ペイロード部分をbase64デコード
      const payload = parts[1];
      const decodedPayload = atob(payload);
      const parsedPayload = JSON.parse(decodedPayload) as UserPayload;

      return parsedPayload;
    } catch (error) {
      console.error('[useUser] Failed to decode JWT:', error);
      return null;
    }
  }, [token]);

  const isAdmin = user?.role === 'admin';
  const isUser = user?.role === 'user';
  const isNewUser = user?.is_new_user === true;
  const isPlatformOrg = user?.is_platform === true;
  // partner owner org の admin (= /org-admin/* と全顧客代行に必要な権限)
  const isPartnerAdmin = user?.is_partner_admin === true;
  // 外部委託デザイナーなど、grant 経由で限定的に代行できるユーザー (partner admin ではない)
  const hasImpersonationGrants = user?.has_impersonation_grants === true;
  // 代行を開始する権利がある (partner admin もしくは grant 保有 designer)
  const canImpersonate = isPartnerAdmin || hasImpersonationGrants;
  // 本人が LightMedia 組織の active admin (identity ベース・組織コンテキスト非依存)。
  // take8.jp 等パートナードメインの組織コンテキストでも社内ベータ機能を表示する判定に使う。
  // バックエンドは require_platform_admin_membership で毎リクエスト DB 照合するため表示用。
  const hasPlatformAdminMembership = user?.has_platform_admin_membership === true;

  return {
    user,
    isAdmin,
    isUser,
    isNewUser,
    isPlatformOrg,
    isPartnerAdmin,
    hasImpersonationGrants,
    canImpersonate,
    hasPlatformAdminMembership,
    isAuthenticated: !!user,
  };
}
